import React from 'react';
import { GetServerSideProps } from 'next';
import BlogHeader from '@/components/blog/BlogHeader';
import BlogBreadCrumbs from '@/components/blog/BlogBreadcrumbs';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { BlogEnum } from '@/types/translations/BlogEnum';
import getAuthorsServerSideProps from '@/lib/serverSideProps/serverSidePropsPages/getAuthorsServerSideProps';
import Container from '@/components/Container';
import Image from 'next/image';
import { FaArrowRight } from 'react-icons/fa';
import DefaultLink from '@/components/common/default/DefaultLink';
import NoDataCard from '@/components/common/NoDataCard';
import { useFetchBlogPosts } from '@/useQuery/queries/blogs';
import strapiConstants from '@/constants/strapi.constants';
import { useRouter } from 'next/router';
import BlogLatestBlogPosts from '@/components/blog/BlogLatestBlogPosts';
import { AuthorsServerProps } from '@/types/props/pages';
import AuthorsMeta from '@/components/seo/meta/AuthorsMeta';
const Authors = ({ blogContributors }: AuthorsServerProps) => {
  const router = useRouter();
  const { translate } = useTypedTranslation<BlogEnum>('blog');
  const blogsQuery = useFetchBlogPosts(strapiConstants.queries.allBlogs, router.locale || 'en');
  const blogs = blogsQuery.data || [];

  return (
    <>
      <AuthorsMeta />
      <BlogHeader />
      <BlogBreadCrumbs shareTitle={translate(BlogEnum.authors_share_title)} isAuthorsPage={true} />
      <Container>
        <div className="tw-pt-12">
          <div className="tw-w-fit max-sm:tw-mx-auto">
            <h1 className="tw-font-prata tw-text-3xl tw-font-normal tw-text-text max-nine:tw-text-2xl">
              {translate(BlogEnum.authors)}
            </h1>
            <hr
              className="tw-m-0 tw-my-4 tw-h-1 tw-w-4/6 tw-rounded-full tw-border-t-0 tw-border-none tw-bg-custom-gold tw-p-0 tw-opacity-100 max-sm:tw-mx-auto max-sm:tw-mt-2"
              aria-hidden="true"
            />{' '}
          </div>
          <p className="tw-m-0 tw-p-0 tw-font-plusJakartaSans tw-text-base tw-font-medium tw-text-slate-gray max-sm:tw-text-center">
            {translate(BlogEnum.authors_subtitle_1)}
            <br />
            <br />
            {translate(BlogEnum.authors_subtitle_2)}
          </p>
        </div>

        <div className="tw-pt-14">
          {blogContributors.length === 0 && <NoDataCard type="authors" />}

          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4 child:tw-basis-[calc(50%-1rem)] max-md:child:tw-basis-full">
            {blogContributors.map(contributor => (
              <div
                key={contributor.id}
                className="tw-flex tw-items-start tw-justify-start tw-gap-12 tw-rounded-xl tw-bg-customGray tw-p-6"
              >
                <div className="tw-flex tw-h-40 tw-w-40 tw-shrink-0 max-nine:tw-h-20 max-nine:tw-w-20">
                  <Image
                    src={contributor.mainImageUrl}
                    width={162}
                    height={162}
                    alt={contributor.name}
                    className="tw-h-full tw-w-full tw-rounded-full tw-object-cover"
                  />
                </div>
                <div>
                  <h3 className="tw-mb-2 tw-text-lg tw-font-semibold tw-text-text tw-underline tw-underline-offset-4 max-nine:tw-text-sm">
                    {contributor.name}
                  </h3>
                  <p className="tw-line-clamp-6 tw-text-base tw-font-medium tw-text-text max-nine:tw-text-xs">
                    {contributor.description}
                  </p>
                  <DefaultLink
                    href={`/blog/authors/${contributor.slug}`}
                    className="tw-text-base tw-font-semibold tw-text-main max-nine:tw-text-sm"
                  >
                    {translate(BlogEnum.to_the_author)}
                    <FaArrowRight className="tw-ml-4" />
                  </DefaultLink>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Container>

      <div className="tw-mb-12 tw-pt-12">
        <BlogLatestBlogPosts blogs={blogs} blogsLoading={blogsQuery.isLoading} oneLine={true} />
      </div>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = getAuthorsServerSideProps;

export default Authors;
